import { css } from '@emotion/react';
import { Fragment } from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type ReportDemographicData } from '~/exportReport/exportReportData.helpers';
import {
  ExportReportMapComponent, type ExportReportMapProps,
} from '~/exportReport/map/exportReportMap.component';
import { ExportReportNotesAndReferencesComponent } from '~/exportReport/notesAndReferences/exportReportNotesAndReferences.component';
import {
  ExportReportSummaryComponent, type ExportReportSummaryProps,
} from '~/exportReport/summary/exportReportSummary.component';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import { noteDefinitions } from '~/exportReport/template/noteDefinitions';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateTableRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateTableRenderer.component';

const tableStyle = css({
  marginTop: 10,
});

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const reportMapStyle = (theme: Theme) => css({
  border: `1px solid ${theme.borderColors.primary}`,
  borderRadius: 4,
  overflow: 'hidden',
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

type ExportReportTemplateRendererProps = Readonly<{
  template: ReportTemplate;
  dataSourceColumns: ReadonlyArray<ReportTableColumn>;
  summaryProps: ExportReportSummaryProps;
  onTableRowClick?: (itemIndex: number, rowIndex: number) => void;
  onMapLoaded?: () => void;
  mapProps: Pick<ExportReportMapProps, 'type' | 'center' | 'paths' | 'radiusInMeters'>;
  roadmapProps?: Partial<ExportReportMapProps>;
  hybridMapProps?: Partial<ExportReportMapProps>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicData: (columnId: string, demographicId: string) => ReportDemographicData | null;
  isPreviewMode?: boolean;
  isLoading?: boolean;
}>;

export const ExportReportTemplateRendererComponent = ({
  template, summaryProps, onTableRowClick, onMapLoaded, mapProps, dataSourceColumns, getDemographicLabel, getDemographicData,
  isPreviewMode, isLoading, roadmapProps, hybridMapProps,
}: ExportReportTemplateRendererProps) => {
  return (
    <>
      <ExportReportSummaryComponent {...summaryProps} />

      {template.items.map((templateItem, itemIndex) => {
        switch (templateItem.type) {
          case 'map': {
            return (
              <div key={`maps-${itemIndex}`} css={mapsWrapperStyle}>
                <ExportReportMapComponent
                  onMapLoaded={onMapLoaded}
                  id={`map${itemIndex}_roadmap`}
                  mapType="roadmap"
                  css={reportMapStyle}
                  {...mapProps}
                  {...roadmapProps}
                />

                <ExportReportMapComponent
                  id={`map${itemIndex}_hybrid`}
                  mapType="hybrid"
                  css={reportMapStyle}
                  {...mapProps}
                  {...hybridMapProps}
                />
              </div>
            );
          }

          case 'page-break': {
            return (
              <div key={`page-break-${itemIndex}`} css={pageBreakStyle} />
            );
          }

          case 'line-break': {
            return (
              <div key={`line-break-${itemIndex}`}>
                <br />
              </div>
            );
          }

          case 'title': {
            return (
              <div key={`title-${itemIndex}`} css={tableTitleStyle}>{templateItem.content}</div>
            );
          }

          case 'table': {
            const itemTableColumns: ReadonlyArray<ReportTableColumn> = templateItem.columns
              ? templateItem.columns.map(column => ({
                id: column.id,
                type: 'custom',
                heading: column.label,
                secondaryHeading: '',
              }))
              : dataSourceColumns;

            const commonProps = {
              css: tableStyle,
              tableColumns: itemTableColumns,
              templateTable: templateItem,
              onTableRowClick,
              itemIndex,
              dataSourceColumns,
              isPreviewMode,
              isLoading,
              getDemographicData,
              getDemographicLabel,
            };

            if (templateItem.layout === 'RenderDataInASeparateTable') {
              return (
                <Fragment key={`custom-table-${itemIndex}`}>
                  {dataSourceColumns.map((dataSourceColumn, index) => {
                    return (
                      <Fragment key={`data-source-column-${dataSourceColumn.id}-${index}`}>
                        <div css={tableTitleStyle}>{dataSourceColumn.heading} {dataSourceColumn.secondaryHeading}</div>
                        <ExportReportTemplateTableRendererComponent
                          {...commonProps}
                          currentDataSource={dataSourceColumn}
                        />

                        <div css={pageBreakStyle} />
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            }

            return (
              <ExportReportTemplateTableRendererComponent
                key={`table-${itemIndex}`}
                {...commonProps}
              />
            );
          }

          case 'notes-and-references': {
            const notes = (templateItem.notes ?? [])
              .map(({ id }) => noteDefinitions.notes[id])
              .filter(notNullsy);

            const references = (templateItem.references ?? [])
              .map(({ id }) => noteDefinitions.references[id])
              .filter(notNullsy);

            return (
              <ExportReportNotesAndReferencesComponent
                key={`notes-and-references-${itemIndex}`}
                notes={notes}
                references={references}
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
